// =====================================================
// TEMPLATE VISIBILITY DROPDOWN
// Unified visibility management for templates
// =====================================================

import React, { useState } from 'react';
import { Template } from '../../services/templateService';
import { templateService } from '../../services/templateService';
import { useLanguage } from '../../state/language/LanguageProvider';

interface TemplateVisibilityDropdownProps {
  template: Template;
  onVisibilityChange?: (newVisibility: 'DRAFT' | 'PRIVATE' | 'PUBLIC') => void | Promise<void>;
  onManageAccess?: () => void;
}

export const TemplateVisibilityDropdown: React.FC<TemplateVisibilityDropdownProps> = ({
  template,
  onVisibilityChange,
  onManageAccess
}) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Use the actual marketplace_visibility value from the template
  const initialVisibility = template.marketplace_visibility || 'DRAFT';
  const [currentVisibility, setCurrentVisibility] = useState<'DRAFT' | 'PRIVATE' | 'PUBLIC'>(
    initialVisibility as 'DRAFT' | 'PRIVATE' | 'PUBLIC'
  );

  const handleVisibilityChange = async (newVisibility: 'DRAFT' | 'PRIVATE' | 'PUBLIC') => {
    if (newVisibility === currentVisibility) {
      setIsOpen(false);
      return;
    }

    setIsUpdating(true);
    try {
      await templateService.updateVisibility(template.id, newVisibility);
      setCurrentVisibility(newVisibility);
      setIsOpen(false);
      if (onVisibilityChange) {
        onVisibilityChange(newVisibility);
      }
    } catch (error) {
      console.error('Error updating visibility:', error);
      alert(t('templates.visibility.failedToUpdate') || 'Failed to update template visibility. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const getVisibilityLabel = (visibility: 'DRAFT' | 'PRIVATE' | 'PUBLIC'): string => {
    // Always show the actual marketplace_visibility value
    switch (visibility) {
      case 'DRAFT':
        return t('templates.draft') || 'Draft';
      case 'PRIVATE':
        return t('templates.private') || 'Private';
      case 'PUBLIC':
        return t('templates.public') || 'Public';
      default:
        return t('templates.draft') || 'Draft';
    }
  };

  const getVisibilityColor = (visibility: 'DRAFT' | 'PRIVATE' | 'PUBLIC'): string => {
    switch (visibility) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'PRIVATE':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'PUBLIC':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getVisibilityIcon = (visibility: 'DRAFT' | 'PRIVATE' | 'PUBLIC'): string => {
    switch (visibility) {
      case 'DRAFT':
        return '📝';
      case 'PRIVATE':
        return '🔒';
      case 'PUBLIC':
        return '🌐';
      default:
        return '📝';
    }
  };

  return (
    <div className="relative inline-block text-left">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isUpdating}
        className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
          getVisibilityColor(currentVisibility)
        } ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span className="mr-2">{getVisibilityIcon(currentVisibility)}</span>
        {getVisibilityLabel(currentVisibility)}
        <svg
          className={`ml-2 -mr-1 h-4 w-4 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          ></div>

          {/* Dropdown Menu */}
          <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
            <div className="py-1" role="menu">
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase border-b">
                {t('templates.visibility.changeVisibility') || 'Change Visibility'}
              </div>

              {/* Draft Option */}
              <button
                onClick={() => handleVisibilityChange('DRAFT')}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center ${
                  currentVisibility === 'DRAFT' ? 'bg-gray-50 font-medium' : ''
                }`}
                role="menuitem"
              >
                <span className="mr-2">📝</span>
                <div>
                  <div className="font-medium">{t('templates.draft') || 'Draft'}</div>
                  <div className="text-xs text-gray-500">{t('templates.visibility.draftDescription') || 'Not published, only you can see it'}</div>
                </div>
              </button>

              {/* Private Option */}
              <button
                onClick={() => handleVisibilityChange('PRIVATE')}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center ${
                  currentVisibility === 'PRIVATE' ? 'bg-gray-50 font-medium' : ''
                }`}
                role="menuitem"
              >
                <span className="mr-2">🔒</span>
                <div>
                  <div className="font-medium">{t('templates.private') || 'Private'}</div>
                  <div className="text-xs text-gray-500">{t('templates.visibility.privateDescription') || 'Visible in marketplace, requires access'}</div>
                </div>
              </button>

              {/* Public Option */}
              <button
                onClick={() => handleVisibilityChange('PUBLIC')}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center ${
                  currentVisibility === 'PUBLIC' ? 'bg-gray-50 font-medium' : ''
                }`}
                role="menuitem"
              >
                <span className="mr-2">🌐</span>
                <div>
                  <div className="font-medium">{t('templates.public') || 'Public'}</div>
                  <div className="text-xs text-gray-500">{t('templates.visibility.publicDescription') || 'Visible and accessible to everyone'}</div>
                </div>
              </button>

              {/* Manage Access (only for Private templates) */}
              {currentVisibility === 'PRIVATE' && onManageAccess && (
                <>
                  <div className="border-t my-1"></div>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      onManageAccess();
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center text-blue-600"
                    role="menuitem"
                  >
                    <span className="mr-2">👥</span>
                    <div>
                      <div className="font-medium">{t('templates.visibility.manageAccess') || 'Manage Access'}</div>
                      <div className="text-xs text-gray-500">{t('templates.visibility.manageAccessDescription') || 'Grant or revoke user/tenant access'}</div>
                    </div>
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

